// src/components/resources/tools/generators/ColorPalette/ColorPaletteGenerator.tsx

/**
 * @fileoverview Enhanced Color Palette Generator Component
 * @description A comprehensive color palette generator with advanced features,
 * type safety, and professional-grade color analysis capabilities.
 *
 * DESIGN PHILOSOPHY:
 * This component prioritizes user intent and color accuracy over algorithmic complexity.
 * The key principle is that the user's base color choice should be respected and reflected
 * in the final palette, with adjustments only applied when explicitly requested.
 *
 * KEY IMPROVEMENTS MADE:
 * 1. Single-step interface: Removed confusing multi-step wizard for better UX
 * 2. Base color preservation: Default "original" options preserve user's actual color choice
 * 3. Responsive generation: All form changes immediately affect the palette output
 *
 * TECHNICAL ARCHITECTURE:
 * - Unified generation function that respects user preferences
 * - Real-time updates with debouncing for performance
 * - Subcomponents (FormField / ColorPalettePreview / ColorPaletteOutput /
 *   ColorSwatch), the form-value guards, the pure palette analysis/export
 *   helpers, and the shared types live in sibling modules (A11/C1 decomposition).
 *
 * @module ColorPaletteGenerator
 */

'use client'

import { useState, useCallback, useMemo, useEffect } from 'react'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import {
  EyeIcon,
  EyeSlashIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  SwatchIcon,
  PaintBrushIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { useStepScrollReset } from '../shared/useStepScrollReset'
import { colorPaletteGenerator } from '~/lib/resources/data/generators/colorPaletteGenerator'
import {
  type GeneratorField,
  type Condition
} from '~/lib/resources/types/tools/generator'
import {
  formatText,
  CapitalizationStrategy,
  DEFAULT_SPECIAL_CASES
} from '~/lib/utils/formatters'
import {
  formatRgbString,
  formatHslString,
  parseColorString
} from '~/lib/utils/colors'
import { rgbToHsl } from '~/lib/utils/colors/transforms'
import { getNumber, isString, isNumber, isBoolean } from '~/lib/utils/form-values'
import { clientLogger, logClientError } from '~/lib/logger-client'
import { FormField } from './FormField'
import { ColorPalettePreview } from './ColorPalettePreview'
import { ColorPaletteOutput } from './ColorPaletteOutput'
import { buildPalette } from './palette-build'
import { exportPalette } from './palette-export'
import type { ColorAnalysis, ColorFormat, ColorInfo, ExportConfig } from './types'

/**
 * Main color palette generator component
 * Provides a full-screen container for the generator interface
 *
 * @returns {JSX.Element} The main generator component
 */
export function ColorPaletteGenerator(): JSX.Element {
  return <ColorPaletteGeneratorContent />
}

/**
 * Content component that manages the generator state and UI
 * Handles form data, validation, step navigation, and palette generation
 *
 * @returns {JSX.Element} The main content component
 */
function ColorPaletteGeneratorContent(): JSX.Element {
  // Form state management
  const [formData, setFormData] = useState<Record<string, unknown>>({})
  const [currentStep, setCurrentStep] = useState(0)
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  // Initialize engagement timers - memoized for stable references and optimal performance
  const [getTotalEngagementMs, getDeltaEngagementMs] = useMemo(
    () => createEngagementTimers(),
    []
  )
  const [localValidationErrors, setLocalValidationErrors] = useState<Record<string, string>>({})
  const [showPreview, setShowPreview] = useState(true) // Default to showing preview
  const [isGenerated, setIsGenerated] = useState(false)
  const [copiedColor, setCopiedColor] = useState<string | null>(null)
  const [exportFormat, setExportFormat] = useState<ColorFormat>('hex')
  // Enhanced state management for better UX
  const [generatedColors, setGeneratedColors] = useState<ColorInfo[]>([])
  const [colorAnalysis, setColorAnalysis] = useState<ColorAnalysis | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exportConfig] = useState<ExportConfig>({
    format: 'hex',
    includeMetadata: true,
    includeVariations: false,
    includeAnalysis: true
  })

  // Get steps from colorPaletteGenerator config
  const steps = colorPaletteGenerator.config.steps
  const currentStepConfig = steps[currentStep]

  // Scroll reset hook for mobile UX
  // Scroll newly rendered steps back to the top
  const stepContainerRef = useStepScrollReset<HTMLDivElement>(currentStep)

  // Special cases for text formatting - merged with defaults from formatters.ts
  // Currently used for proper capitalization of color harmony types and accessibility levels
  const specialCases = useMemo(() => ({
    ...DEFAULT_SPECIAL_CASES,
    // Color-specific terms for proper capitalization in UI display
    'wcag': 'WCAG',
    'aa': 'AA',
    'aaa': 'AAA',
    'rgb': 'RGB',
    'hex': 'HEX',
    'hsl': 'HSL',
    'hsv': 'HSV',
    'hwb': 'HWB',
    'cmyk': 'CMYK',
    'xyz': 'XYZ',
    'lab': 'LAB',
    'oklab': 'OKLAB',
    'lch': 'LCH',
    'split-complementary': 'Split-Complementary',
  }), [])

  /**
   * Format text with color-specific special cases for proper capitalization
   * Used for displaying color harmony types, accessibility levels, and other color terminology
   * @param text - The text to format
   * @param capitalizationStrategy - The capitalization strategy to use
   * @returns Formatted text with proper capitalization
   */
  const formatWithSpecialCases = useCallback((text: string, capitalizationStrategy = CapitalizationStrategy.TITLE_CASE) => {
    return formatText(text, {
      capitalization: capitalizationStrategy,
      specialCases
    })
  }, [specialCases])

  /**
   * Helper function to evaluate a condition against form data
   * @param condition - The condition to evaluate
   * @param data - The form data to evaluate against
   * @returns boolean indicating if the condition is met
   */
  const evaluateCondition = useCallback((condition: Condition, data: Record<string, unknown>): boolean => {
    if (!condition) return true

    const fieldValue = data[condition.field]
    const conditionValue = condition.value

    switch (condition.operator) {
      case 'equals':
        return fieldValue === conditionValue
      case 'notEquals':
        return fieldValue !== conditionValue
      case 'contains':
        return isString(fieldValue) && fieldValue.includes(String(conditionValue))
      case 'notContains':
        return isString(fieldValue) && !fieldValue.includes(String(conditionValue))
      case 'greaterThan':
        return getNumber(fieldValue) > getNumber(conditionValue)
      case 'lessThan':
        return getNumber(fieldValue) < getNumber(conditionValue)
      case 'greaterThanOrEquals':
        return getNumber(fieldValue) >= getNumber(conditionValue)
      case 'lessThanOrEquals':
        return getNumber(fieldValue) <= getNumber(conditionValue)
      case 'isEmpty':
        return !fieldValue || (isString(fieldValue) && fieldValue.trim() === '')
      case 'isNotEmpty':
        return Boolean(fieldValue) && (!isString(fieldValue) || fieldValue.trim() !== '')
      case 'exists':
        return fieldValue !== undefined && fieldValue !== null
      case 'notExists':
        return fieldValue === undefined || fieldValue === null
      default:
        return true
    }
  }, [])

  /**
   * Check if a field should be shown based on conditions
   * @param field - The field to check
   * @returns boolean indicating if the field should be shown
   */
  const shouldShowField = useCallback((field: GeneratorField): boolean => {
    if (!field.showIf) return true
    return evaluateCondition(field.showIf, formData)
  }, [formData, evaluateCondition])

  /**
   * Check if a field is required based on conditions
   * @param field - The field to check
   * @returns boolean indicating if the field is required
   */
  const isFieldRequired = useCallback((field: GeneratorField): boolean => {
    if (field.validation?.required) return true
    if (field.validation?.requiredIf) {
      return evaluateCondition(field.validation.requiredIf, formData)
    }
    return false
  }, [formData, evaluateCondition])

  /**
   * Get the effective value for a field, considering both form data and default values
   * @param field - The field to get the value for
   * @returns The effective value (form data value or default value)
   */
  const getFieldValue = useCallback((field: GeneratorField): unknown => {
    const formValue = formData[field.id]
    // If there's a form value, use it
    if (formValue !== undefined && formValue !== null) {
      return formValue
    }
    // Otherwise, use the default value
    return field.defaultValue
  }, [formData])

  /**
   * Check if a field value is empty, considering its type and default value
   * @param {GeneratorField} field - The field to check (unused)
   * @param {unknown} value - The value to check
   * @returns {boolean} boolean indicating if the value is considered empty
   */
  const isFieldValueEmpty = useCallback((field: GeneratorField, value: unknown): boolean => {
    void field // explicitly ignore unused variable to avoid TS6133

    // If value is null or undefined, it's empty
    if (value === undefined || value === null) {
      return true
    }

    // For strings, check if empty or just whitespace
    if (isString(value)) {
      return value.trim() === ''
    }

    // For arrays, check if empty
    if (Array.isArray(value)) {
      return value.length === 0
    }

    // For numbers, 0 is not considered empty unless it's explicitly invalid
    if (isNumber(value)) {
      return false
    }

    // For booleans, false is not considered empty
    if (isBoolean(value)) {
      return false
    }

    // For other types, consider them not empty if they exist
    return false
  }, [])

  /**
   * Update form data for a specific field
   * @param fieldId - The ID of the field to update
   * @param value - The new value for the field
   */
  const updateField = useCallback((fieldId: string, value: unknown): void => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }))

    // Clear validation error for this field using object destructuring to avoid dynamic delete
    setLocalValidationErrors(prev => {
      const { [fieldId]: _, ...newErrors } = prev
      return newErrors
    })
  }, [])

  /**
   * Validate the current step's fields
   * @returns boolean indicating if the step is valid
   */
  const validateStep = useCallback((): boolean => {
    // Early return if no current step config to prevent undefined access
    if (!currentStepConfig) {
      clientLogger.error('Cannot validate step: currentStepConfig is undefined', { component: 'ColorPaletteGenerator' })
      return false
    }

    const errors: Record<string, string> = {}
    let isValid = true

    // Now TypeScript knows currentStepConfig is defined
    currentStepConfig.fields.forEach(field => {
      if (!shouldShowField(field)) return

      const isRequired = isFieldRequired(field)
      const effectiveValue = getFieldValue(field)

      if (isRequired && isFieldValueEmpty(field, effectiveValue)) {
        errors[field.id] = `${field.label} is required`
        isValid = false
      }

      // Additional validation based on field type
      if (effectiveValue && field.validation) {
        if (field.validation.min && getNumber(effectiveValue) < field.validation.min) {
          errors[field.id] = `${field.label} must be at least ${field.validation.min}`
          isValid = false
        }
        if (field.validation.max && getNumber(effectiveValue) > field.validation.max) {
          errors[field.id] = `${field.label} must be at most ${field.validation.max}`
          isValid = false
        }
      }

      // Color field validation
      if (field.type === 'color' && effectiveValue && isString(effectiveValue)) {
        const parsedColor = parseColorString(effectiveValue)
        if (!parsedColor) {
          errors[field.id] = `${field.label} must be a valid color`
          isValid = false
        }
      }
    })

    setLocalValidationErrors(errors)
    setShowValidationErrors(true)
    return isValid
  }, [currentStepConfig, shouldShowField, isFieldRequired, getFieldValue, isFieldValueEmpty])



  /**
   * Reset the generator to initial state
   */
  const resetGenerator = useCallback((): void => {
    setFormData({})
    setCurrentStep(0)
    setIsGenerated(false)
    setShowValidationErrors(false)
    setLocalValidationErrors({})
    setCopiedColor(null)
    setGeneratedColors([])
    setColorAnalysis(null)
    setError(null)
    setIsGenerating(false)
  }, [])

  /**
   * Copy color to clipboard
   */
  const copyColor = useCallback(async (color: string, format: ColorFormat = 'hex'): Promise<void> => {
    // Track color copy - user copied a color value from the palette
    sendResourceEvent('interact', {
      resourceType: 'tool',
      resourceSlug: 'color-palette-generator',
      engagementMs: getDeltaEngagementMs(),
      params: {
        tool_type: 'generator',
        interaction_type: 'copy_color',
        color_format: format
      }
    })

    try {
      let formattedColor = color

      // Format color based on selected format
      const rgb = parseColorString(color)
      if (rgb) {
        switch (format) {
          case 'rgb':
            formattedColor = formatRgbString(rgb)
            break
          case 'hsl':
            formattedColor = formatHslString(rgbToHsl(rgb))
            break
          case 'css':
            formattedColor = `--color: ${color};`
            break
          case 'scss':
            formattedColor = `$color: ${color};`
            break
          case 'tailwind':
            formattedColor = `'custom': '${color}'`
            break
          case 'hex':
          case 'figma':
          case 'json':
          case 'sketch':
          default:
            formattedColor = color
        }
      }

      await navigator.clipboard.writeText(formattedColor)
      setCopiedColor(color)
      setTimeout(() => setCopiedColor(null), 2000)
    } catch (error) {
      logClientError(error, { component: 'ColorPaletteGenerator', context: 'Failed to copy color' })
    }
  }, [getDeltaEngagementMs])

  /**
   * Download the generated palette
   */
  const downloadPalette = useCallback((colors: ColorInfo[]): void => {
    // Track palette download - user exported the color palette
    sendResourceEvent('complete', {
      resourceType: 'tool',
      resourceSlug: 'color-palette-generator',
      engagementMs: getTotalEngagementMs(),
      params: {
        tool_type: 'generator',
        export_format: exportFormat,
        color_count: colors.length,
        outcome: 'success'
      }
    })

    const content = exportPalette(colors, exportFormat, exportConfig)
    const filename = `color-palette.${exportFormat === 'json' ? 'json' : 'txt'}`

    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [exportFormat, exportConfig, getTotalEngagementMs])

  /**
   * FIXED: Proper palette generation using existing utilities
   *
   * Uses the harmony functions from color-relationships.ts to generate
   * the correct colors for each harmony type without duplication.
   */
  const generateAdvancedPalette = useCallback((formData: Record<string, unknown>): Promise<ColorInfo[]> => {
    setIsGenerating(true)
    setError(null)

    try {
      // The pure generation core lives in ./palette-build (testable in isolation);
      // this wrapper only owns the React state bookkeeping.
      const { colors, analysis } = buildPalette(formData)
      setColorAnalysis(analysis)
      setGeneratedColors(colors)

      return Promise.resolve(colors)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate palette'
      setError(errorMessage)
      throw error
    } finally {
      setIsGenerating(false)
    }
  }, [])

  /**
   * Generate the color palette with enhanced features
   */
  const generatePalette = useCallback(async (): Promise<void> => {
    if (validateStep()) {
      try {
        await generateAdvancedPalette(formData)
        setIsGenerated(true)
      } catch (error) {
        logClientError(error, { component: 'ColorPaletteGenerator', context: 'Failed to generate palette' })
        // Error is already set in generateAdvancedPalette
      }
    }
  }, [validateStep, generateAdvancedPalette, formData])

  // Toggle preview visibility
  const togglePreview = useCallback((): void => {
    setShowPreview(prev => !prev)
  }, [])

  /**
   * Auto-regenerate palette when form data changes (with debouncing)
   *
   * FIXED: Now generates palette immediately on component mount with default values
   * and updates when any form field changes, providing real-time feedback to users.
   *
   * This ensures:
   * - Initial palette generation with default values
   * - Palette size changes immediately affect the number of colors
   * - Accessibility changes immediately affect color generation
   * - Saturation/lightness changes immediately affect the palette
   * - Harmony type changes immediately affect the color relationships
   */
  useEffect(() => {
    // Generate palette immediately if we have any form data (including defaults)
    if (Object.keys(formData).length > 0) {
      const timeoutId = setTimeout(() => {
        generateAdvancedPalette(formData).catch(error => {
          logClientError(error, { component: 'ColorPaletteGenerator', context: 'Auto-regeneration failed' })
        })
      }, 200) // 200ms debounce for more responsive updates

      return () => clearTimeout(timeoutId)
    }
  }, [formData, generateAdvancedPalette])

  // Log the invalid-step-config invariant from an effect (once per occurrence),
  // NOT from the render body below — a render-path drain POST would re-fire on
  // every re-render while the Configuration Error UI stays mounted.
  useEffect(() => {
    if (!currentStepConfig) {
      clientLogger.error(`Invalid step index: ${currentStep}`, { component: 'ColorPaletteGenerator' })
    }
  }, [currentStepConfig, currentStep])

  // Validate that we have a valid step configuration
  if (!currentStepConfig) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Configuration Error</h2>
          <p className="mt-2 text-gray-600">Invalid step configuration. Please check the generator setup.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-10">
      {/* The scroll reset hook targets this wrapper so both the form and sticky preview realign to the top on mobile without breaking the desktop split layout. */}
      <div
        ref={stepContainerRef}
        className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-1 sm:px-0 lg:flex-row lg:items-start"
      >
        <section className="flex-1 rounded-3xl border border-slate-200/70 bg-white/80 p-6 shadow-2xl dark:border-slate-800/70 dark:bg-slate-900/70">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
              Color Palette Generator
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Configure your color palette with harmony, accessibility, and style preferences
            </p>
          </div>

          <div className="space-y-6">
            {/*
              SINGLE-STEP INTERFACE: All fields from all steps are now displayed together
              This provides better UX by allowing users to see all options and their
              relationships at once, rather than being forced through a confusing
              multi-step wizard that hid important context.

              The flatMap ensures all fields from all steps are available in one view.
            */}
            {steps.flatMap(step => step.fields).map(field => {
              if (!shouldShowField(field)) return null

              const isRequired = isFieldRequired(field)
              const hasError = showValidationErrors && localValidationErrors[field.id]
              const value = getFieldValue(field)

              return (
                <div key={field.id} className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                    {field.label}
                    {isRequired && <span className="ml-1 text-rose-500">*</span>}
                  </label>
                  {field.description ? (
                    <p className="text-xs text-slate-500 dark:text-slate-400">{field.description}</p>
                  ) : null}

                  <FormField
                    field={field}
                    showValidationError={Boolean(hasError)}
                    isRequired={isRequired}
                    value={value}
                    onChange={newValue => updateField(field.id, newValue)}
                  />

                  {hasError ? (
                    <p className="text-sm text-rose-500 dark:text-rose-300">{localValidationErrors[field.id]}</p>
                  ) : null}
                </div>
              )
            })}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
              <button
                onClick={togglePreview}
                className="flex items-center gap-2 rounded-full border border-slate-300/70 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-indigo-200 hover:text-indigo-600 dark:border-slate-600 dark:text-slate-200 dark:hover:border-indigo-500/40 dark:hover:text-indigo-200"
              >
                {showPreview ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                {showPreview ? 'Hide preview' : 'Show preview'}
              </button>

            <div className="flex items-center gap-3">
                <button
                onClick={resetGenerator}
                className="flex items-center gap-2 rounded-full border border-slate-300/70 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-red-200 hover:text-red-600 dark:border-slate-600 dark:text-slate-200 dark:hover:border-red-500/40 dark:hover:text-red-200"
                >
                Reset
                </button>

                <button
                onClick={generatePalette}
                disabled={isGenerating}
                className={clsx(
                  "flex items-center gap-2 rounded-full px-6 py-2 text-sm font-semibold text-white shadow-sm transition",
                  isGenerating
                    ? "bg-indigo-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700"
                )}
              >
                {isGenerating ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Generating...
                  </>
                ) : (
                  <>
                    <SwatchIcon className="h-4 w-4" />
                    Generate palette
                  </>
                )}
              </button>
            </div>
          </div>
        </section>

        {showPreview ? (
          <aside className="lg:sticky lg:top-24 lg:w-[22rem]">
            <div className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl dark:border-slate-700/70 dark:bg-slate-900/70">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">Preview</h3>
                <button
                  type="button"
                  onClick={togglePreview}
                  className="rounded-full p-2 text-slate-400 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200"
                  aria-label="Hide preview"
                >
                  <EyeSlashIcon className="h-5 w-5" />
                </button>
              </div>
              <ColorPalettePreview
                formData={formData}
                formatText={formatWithSpecialCases}
                onCopyColor={copyColor}
                copiedColor={copiedColor}
                getFieldValue={getFieldValue}
                fields={currentStepConfig.fields}
                generatedColors={generatedColors}
              />
            </div>
          </aside>
        ) : null}
      </div>

      {error && (
        <section className="mx-auto w-full max-w-6xl rounded-3xl border border-red-200/70 bg-red-50/95 p-6 shadow-2xl dark:border-red-800/70 dark:bg-red-900/20">
          <div className="flex items-center gap-3">
            <XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
            <div>
              <h3 className="text-lg font-semibold text-red-900 dark:text-red-50">Generation Error</h3>
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </section>
      )}

      {isGenerated && generatedColors.length > 0 ? (
        <section className="mx-auto w-full max-w-6xl rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-2xl dark:border-slate-700/70 dark:bg-slate-900/70">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">Your Color Palette</h3>
              {colorAnalysis && (
                <div className="mt-2 flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <ChartBarIcon className="h-4 w-4 text-indigo-600" />
                    <span className="text-slate-600 dark:text-slate-400">
                      Harmony: {colorAnalysis.harmonyScore}%
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {colorAnalysis.accessibilityLevel === 'AAA' ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 text-red-600" />
                    )}
                    <span className="text-slate-600 dark:text-slate-400">
                      {colorAnalysis.accessibilityLevel}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <PaintBrushIcon className="h-4 w-4 text-orange-600" />
                    <span className="text-slate-600 dark:text-slate-400">
                      {formatWithSpecialCases(colorAnalysis.temperatureBalance)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as ColorFormat)}
                className="rounded-lg border border-slate-300/70 px-3 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-700"
              >
                <option value="hex">HEX</option>
                <option value="rgb">RGB</option>
                <option value="hsl">HSL</option>
                <option value="css">CSS Variables</option>
                <option value="scss">SCSS Variables</option>
                <option value="tailwind">Tailwind Config</option>
                <option value="figma">Figma</option>
                <option value="sketch">Sketch</option>
                <option value="json">JSON</option>
              </select>
              <button
                onClick={() => downloadPalette(generatedColors)}
                className="flex items-center gap-2 rounded-full border border-slate-300/70 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-indigo-200 hover:text-indigo-600 dark:border-slate-600 dark:text-slate-200 dark:hover:border-indigo-500/40 dark:hover:text-indigo-200"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download
              </button>
              <button
                onClick={resetGenerator}
                className="rounded-full border border-slate-300/70 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-indigo-200 hover:text-indigo-600 dark:border-slate-600 dark:text-slate-200 dark:hover:border-indigo-500/40 dark:hover:text-indigo-200"
              >
                Create another
              </button>
            </div>
          </div>
              <ColorPaletteOutput
                formData={formData}
                formatText={formatWithSpecialCases}
            generatedColors={generatedColors}
            colorAnalysis={colorAnalysis}
                onCopyColor={copyColor}
                copiedColor={copiedColor}
                getFieldValue={getFieldValue}
                fields={currentStepConfig.fields}
              />
        </section>
      ) : null}
    </div>
  )
}
